import {UseFormReturn} from 'react-hook-form'
import {BulkOrderFormValues} from '../../BulkOrdersFormValues'
import {useBulkOrderFormChangeHandlerWithMethods} from '../../FormChangeHandlers'
import React from 'react'
import {
  ActiveMaterialDeliveryItem,
  HaulerInfo,
  OrderIntakeMaterialOptionPayload,
  OrderIntakeOption
} from '../../declarations/types'
import {SelectedSplitLoadMaterials} from '../containers/SplitLoad/types'
import {first} from 'lodash'
import {checkEmptyTimeZone, getCurrentTimeStamp} from '../../../Organisms/OrderIntake/utils'
import {useConfirmation} from '../../components/ConfirmationDialog/ConfirmationService'

interface UseFieldHandlersParams {
  methods: UseFormReturn<BulkOrderFormValues>
  formChangeHandlers: ReturnType<typeof useBulkOrderFormChangeHandlerWithMethods>
  materialEditorController: any
  selectedSite?: OrderIntakeOption
  setEditorActivationTimeStamp: (timestamp: string) => void
  setSelectedMaterialIndex: (index: number) => void
  t: (key: string) => string
  shouldRedirectToSummaryPage?: boolean
  setCanShowSummaryPage?: (value: boolean) => void
}

export const useFieldHandlers = ({
  methods,
  formChangeHandlers,
  materialEditorController,
  selectedSite,
  setEditorActivationTimeStamp,
  setSelectedMaterialIndex,
  t,
  shouldRedirectToSummaryPage,
  setCanShowSummaryPage
}: UseFieldHandlersParams) => {
  const confirm = useConfirmation()

  const onHaulerInfoChange = React.useCallback(
    (index: number, haulerInfo?: HaulerInfo) => {
      methods.setValue(`orders.${index}.payload.haulerInfo`, haulerInfo)
    },
    [methods]
  )

  const onCustomerReferenceChange = React.useCallback(
    (index: number, ref: string) => {
      methods.setValue(`orders.${index}.payload.customerReference`, ref)
    },
    [methods]
  )

  const onQuantityChange = React.useCallback(
    (index: number, quantity: number) => {
      methods.setValue(`orders.${index}.payload.capacity.quantity`, quantity)
      methods.setValue(`isOrderRequestConfirmed`, true)
    },
    [methods]
  )

  const onDriverInstructionsChange = React.useCallback(
    (index: number, driverInstructions: string) => {
      methods.setValue(
        `orders.${index}.payload.additionalDriverInfo.driverInstructions`,
        driverInstructions
      )
    },
    [methods]
  )

  const onDispatchNotesChange = React.useCallback(
    (index: number, dispatchNotes: string) => {
      methods.setValue(`orders.${index}.payload.orderAllocatorInstructions`, dispatchNotes)
    },
    [methods]
  )

  const onBomKeyChange = React.useCallback(
    (index: number, bomKey: string | undefined) => {
      const currentBomKeys = methods.getValues('savedBomKeys') || []
      currentBomKeys[index] = bomKey
      methods.setValue('savedBomKeys', currentBomKeys)
    },
    [methods]
  )

  const onMaterialChange = React.useCallback(
    (index: number, selectedMaterialOption: OrderIntakeMaterialOptionPayload) => {
      formChangeHandlers.onMaterialChange(index, selectedMaterialOption)
    },
    [formChangeHandlers]
  )

  const onSplitLoadMaterialsSelected = React.useCallback(
    (index: number, materials: SelectedSplitLoadMaterials) => {
      // Get current array or initialize empty array
      const currentSplitLoadMaterials = methods.getValues('selectedSplitLoadMaterials') || []

      // Set materials for this specific order index
      currentSplitLoadMaterials[index] = materials

      // Update parent form
      methods.setValue('selectedSplitLoadMaterials', currentSplitLoadMaterials, {
        shouldDirty: true,
        shouldValidate: true
      })
    },
    [methods]
  )

  const onEditorActivation = React.useCallback(
    (index: number) => {
      if (!selectedSite) return

      const orderPath = `orders.${index}` as const
      const order = methods.getValues(orderPath)

      const defaultMaterialOption = first(
        selectedSite.materials[order?.payload?.materialEnteredNumber]
      )
      const timeZone = defaultMaterialOption?.businessHours.timeZone
      setEditorActivationTimeStamp(getCurrentTimeStamp(checkEmptyTimeZone(timeZone)))

      methods.setValue('isOrderRequestConfirmed', true)
      setSelectedMaterialIndex(index)
      shouldRedirectToSummaryPage && setCanShowSummaryPage?.(false)
    },
    [
      methods,
      selectedSite,
      setEditorActivationTimeStamp,
      setSelectedMaterialIndex,
      shouldRedirectToSummaryPage,
      setCanShowSummaryPage
    ]
  )

  const handleEditorActivation = React.useCallback(
    (id: string, item: ActiveMaterialDeliveryItem, index: number) => {
      if (materialEditorController.hasActiveMaterialFormChanges) {
        void confirm({
          'data-test-id': 'order-intake-confirmation-dialog',
          title: t('orderIntake.materialOrder.confirmDialog.title'),
          submitButtonTitle: t('orderIntake.materialOrder.confirmDialog.submitButton'),
          cancelButtonTitle: t('orderIntake.materialOrder.confirmDialog.cancelButton'),
          variant: 'discard'
        }).then(() => {
          materialEditorController.setActiveOrderBox(id, item, 'add')
          onEditorActivation(index)
        })
      } else {
        materialEditorController.setActiveOrderBox(id, item, 'add')
        onEditorActivation(index)
      }
    },
    [materialEditorController, confirm, t, onEditorActivation]
  )

  const handleStatusChange = React.useCallback(
    (hasChanges: boolean) => {
      materialEditorController.setChanges(hasChanges)
    },
    [materialEditorController]
  )

  return {
    onHaulerInfoChange,
    onCustomerReferenceChange,
    onQuantityChange,
    onDriverInstructionsChange,
    onDispatchNotesChange,
    onBomKeyChange,
    onMaterialChange,
    onSplitLoadMaterialsSelected,
    handleEditorActivation,
    handleStatusChange
  }
}
